import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, ProtectedRoute } from './hooks/useAuth';
import { ModalProvider } from './hooks/useModal';
import StudentLoginPage from './pages/student/LoginPage';
import ParentLoginPage, {
	ParentChangePasswordPage,
} from './pages/parent/LoginPage';
import TrainerLoginPage from './pages/trainer/LoginPage';
import SchoolAdminLoginPage from './pages/school/LoginPage';
import MasterAdminLoginPage from './pages/admin/LoginPage';
import SalesLoginPage, {
	SalesChangePasswordPage,
} from './pages/sales/LoginPage';

// Route components are code-split so a session only downloads the screens it
// visits. Without this every role shipped the entire app (admin console, sales
// CRM, session player) in one 3.3 MB chunk on first paint.
const StudentDashboard = lazy(() => import('./pages/student/DashboardPage'));
const ParentDashboard = lazy(() => import('./pages/parent/DashboardPage'));
const TrainerDashboard = lazy(() => import('./pages/trainer/DashboardPage'));
const TrainerClassesPage = lazy(() => import('./pages/trainer/ClassesPage'));
const TrainerClassDetail = lazy(
	() => import('./pages/trainer/ClassDetailPage'),
);
const TrainerStudentDetail = lazy(
	() => import('./pages/trainer/StudentDetailPage'),
);
const SchoolAdminSetupPage = lazy(() => import('./pages/school/SetupPage'));
const SchoolAdminDashboard = lazy(() => import('./pages/school/DashboardPage'));
const SchoolClassesPage = lazy(() => import('./pages/school/ClassesPage'));
const SchoolTrainersPage = lazy(() => import('./pages/school/TrainersPage'));
const SchoolStudentsPage = lazy(() => import('./pages/school/StudentsPage'));
const SchoolAnalyticsPage = lazy(() => import('./pages/school/AnalyticsPage'));
const ZonalLoginPage = lazy(() => import('./pages/zonal/LoginPage'));
const ZonalChangePasswordPage = lazy(
	() => import('./pages/zonal/ChangePasswordPage'),
);
const SchoolDailyReportsPage = lazy(
	() => import('./pages/school/DailyReportsPage'),
);
const SchoolLabApprovalPage = lazy(
	() => import('./pages/school/LabApprovalPage'),
);
const OpsDashboardPage = lazy(() => import('./pages/ops/DashboardPage'));
const OpsHandoverQueuePage = lazy(() => import('./pages/ops/HandoverQueuePage'));
const OpsHandoverDetailPage = lazy(
	() => import('./pages/ops/HandoverDetailPage'),
);
const OpsOnboardingPage = lazy(() => import('./pages/ops/OnboardingPage'));
const OpsLabSetupPage = lazy(() => import('./pages/ops/LabSetupPage'));
const OpsLabSetupDetailPage = lazy(
	() => import('./pages/ops/LabSetupDetailPage'),
);
const OpsInventoryPage = lazy(() => import('./pages/ops/InventoryPage'));
const OpsTicketsPage = lazy(() => import('./pages/ops/TicketsPage'));
const OpsTicketDetailPage = lazy(() => import('./pages/ops/TicketDetailPage'));
const MasterAdminDashboard = lazy(() => import('./pages/admin/DashboardPage'));
const AdminSchoolsPage = lazy(() => import('./pages/admin/SchoolsPage'));
const AdminUsersPage = lazy(() => import('./pages/admin/UsersPage'));
const AdminCurriculumPage = lazy(() => import('./pages/admin/CurriculumPage'));
const AdminAnalyticsPage = lazy(() => import('./pages/admin/AnalyticsPage'));
const AdminAuditLogsPage = lazy(() => import('./pages/admin/AuditLogsPage'));
const AdminConfigPage = lazy(() => import('./pages/admin/ConfigPage'));
const AdminKitCatalogPage = lazy(
	() => import('./pages/admin/KitCatalogPage'),
);
const AdminSchoolDetailPage = lazy(
	() => import('./pages/admin/SchoolDetailPage'),
);
const AdminCurriculumDetailPage = lazy(
	() => import('./pages/admin/CurriculumDetailPage'),
);
const AdminSessionPreviewPage = lazy(
	() => import('./pages/admin/SessionPreviewPage'),
);
const SecureDocsPage = lazy(() => import('./pages/admin/SecureDocsPage'));
const DemoRequestsPage = lazy(() => import('./pages/admin/DemoRequestsPage'));
const SecureDocViewerPage = lazy(
	() => import('./pages/shared/SecureDocViewerPage'),
);
const TrainerCurriculaPage = lazy(
	() => import('./pages/trainer/CurriculaPage'),
);
const TrainerCurriculumDetailPage = lazy(
	() => import('./pages/trainer/CurriculumDetailPage'),
);
const TrainerSessionPreviewPage = lazy(
	() => import('./pages/trainer/SessionPreviewPage'),
);
const StudentCurriculaPage = lazy(
	() => import('./pages/student/CurriculaPage'),
);
const StudentCurriculumDetailPage = lazy(
	() => import('./pages/student/CurriculumDetailPage'),
);
const StudentSessionPage = lazy(() => import('./pages/student/SessionPage'));
const StudentProfilePage = lazy(() => import('./pages/student/ProfilePage'));
const ParentAnalyticsPage = lazy(() => import('./pages/parent/AnalyticsPage'));
const TrainerStudentAnalyticsPage = lazy(
	() => import('./pages/trainer/StudentAnalyticsPage'),
);
const TrainerStudentSessionReviewPage = lazy(
	() => import('./pages/trainer/StudentSessionReviewPage'),
);
const TrainerStudentCurriculumProgressPage = lazy(
	() => import('./pages/trainer/StudentCurriculumProgressPage'),
);
const ChildSessionReviewPage = lazy(
	() => import('./pages/parent/ChildSessionReviewPage'),
);
const ChildCurriculumProgressPage = lazy(
	() => import('./pages/parent/ChildCurriculumProgressPage'),
);
const AdminClassCurriculumProgressPage = lazy(
	() => import('./pages/admin/ClassCurriculumProgressPage'),
);
const AdminStudentSessionReviewPage = lazy(
	() => import('./pages/admin/StudentSessionReviewPage'),
);
const AdminStudentCurriculumProgressPage = lazy(
	() => import('./pages/admin/StudentCurriculumProgressPage'),
);
const SchoolClassCurriculumProgressPage = lazy(
	() => import('./pages/school/ClassCurriculumProgressPage'),
);
const SchoolStudentSessionReviewPage = lazy(
	() => import('./pages/school/StudentSessionReviewPage'),
);
const SchoolStudentCurriculumProgressPage = lazy(
	() => import('./pages/school/StudentCurriculumProgressPage'),
);
const AdminTimetablePage = lazy(() => import('./pages/admin/TimetablePage'));
const AdminMonitoringPage = lazy(() => import('./pages/admin/MonitoringPage'));
const AdminRequestsPage = lazy(() => import('./pages/admin/RequestsPage'));
const SchoolTimetablePage = lazy(() => import('./pages/school/TimetablePage'));
const SchoolMonitoringPage = lazy(
	() => import('./pages/school/MonitoringPage'),
);
const ParentTimetablePage = lazy(() => import('./pages/parent/TimetablePage'));
const StudentTimetablePage = lazy(
	() => import('./pages/student/TimetablePage'),
);
const StudentSessionLockScreenPage = lazy(
	() => import('./pages/student/SessionLockScreenPage'),
);
const TrainerTimetableTodayPage = lazy(
	() => import('./pages/trainer/TimetableTodayPage'),
);
const TrainerTimetableWeekPage = lazy(
	() => import('./pages/trainer/TimetableWeekPage'),
);
const TrainerSessionFlowPage = lazy(
	() => import('./pages/trainer/SessionFlowPage'),
);
const TrainerDayReportPage = lazy(
	() => import('./pages/trainer/DayReportPage'),
);
const AdminDailyReportsPage = lazy(
	() => import('./pages/admin/DailyReportsPage'),
);
const AdminDailyReportReviewPage = lazy(
	() => import('./pages/admin/DailyReportReviewPage'),
);
const AdminGalleryPage = lazy(() => import('./pages/admin/GalleryPage'));
const TrainerSecuritySetupPage = lazy(
	() => import('./pages/trainer/SecuritySetupPage'),
);
const SalesDashboardPage = lazy(() => import('./pages/sales/DashboardPage'));
const SalesHubPage = lazy(() => import('./pages/sales/SalesHubPage'));
const SalesActivityTrackerPage = lazy(
	() => import('./pages/sales/ActivityTrackerPage'),
);
const DeletedLeadsPage = lazy(() => import('./pages/sales/DeletedLeadsPage'));
const SalesAppointmentsPage = lazy(
	() => import('./pages/sales/AppointmentsPage'),
);
const SalesAssistantPage = lazy(() => import('./pages/sales/AssistantPage'));
const SalesContactListPage = lazy(
	() => import('./pages/sales/ContactListPage'),
);
const SalesCoordinatorsPage = lazy(
	() => import('./pages/sales/CoordinatorsPage'),
);
const SalesPipelinePage = lazy(() => import('./pages/sales/PipelinePage'));
const SalesLeadDetailPage = lazy(() => import('./pages/sales/LeadDetailPage'));
const SalesProposalsPage = lazy(() => import('./pages/sales/ProposalsPage'));
const SalesMousPage = lazy(() => import('./pages/sales/MousPage'));
const SalesImportPage = lazy(() => import('./pages/sales/ImportPage'));
const SalesUnassignedPage = lazy(() => import('./pages/sales/UnassignedPage'));
const SalesZonesPage = lazy(() => import('./pages/sales/ZonesPage'));
const SalesApprovalsPage = lazy(() => import('./pages/sales/ApprovalsPage'));
const SalesTeamPage = lazy(() => import('./pages/sales/TeamPage'));
const SalesLabTypesPage = lazy(() => import('./pages/sales/LabTypesPage'));
const SalesContentPage = lazy(() => import('./pages/sales/ContentPage'));
const SalesPlaybookPage = lazy(() => import('./pages/sales/PlaybookPage'));
const SalesHandoffPage = lazy(() => import('./pages/sales/HandoffPage'));
const SalesRevivalPage = lazy(() => import('./pages/sales/RevivalPage'));
const MouSignPage = lazy(() => import('./pages/shared/MouSignPage'));
const ProposalViewPage = lazy(() => import('./pages/shared/ProposalViewPage'));

// Shown while a route chunk loads. Deliberately minimal — a full skeleton would
// flash on fast connections where the chunk arrives in a few ms.
function RouteFallback() {
	return (
		<div className='aurora-bg flex min-h-screen items-center justify-center'>
			<div className='h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-transparent' />
		</div>
	);
}

function App() {
	return (
		<ModalProvider>
			<AuthProvider>
				<BrowserRouter>
					<Suspense fallback={<RouteFallback />}>
						<Routes>
							{/* Public */}
							<Route
								path='/'
								element={
									<Navigate
										to={
											localStorage.getItem('alforaa_login_path') ??
											'/student/login'
										}
										replace
									/>
								}
							/>

							{/* Public secure document viewer */}
							<Route
								path='/view-doc/:token'
								element={<SecureDocViewerPage />}
							/>

							{/* Public MOU signing + proposal viewing (school-facing) */}
							<Route path='/mou/:token' element={<MouSignPage />} />
							<Route path='/proposal/:token' element={<ProposalViewPage />} />

							{/* Sales */}
							<Route path='/sales/login' element={<SalesLoginPage />} />
							<Route
								path='/sales/change-password'
								element={
									<ProtectedRoute
										allowedRoles={[
											'salesperson',
											'sales_manager',
											'appointment_coordinator',
										]}
										requiresSetup>
										<SalesChangePasswordPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/sales/hub'
								element={
									<ProtectedRoute allowedRoles={['master_admin']}>
										<SalesHubPage />
									</ProtectedRoute>
								}
							/>
							{/* Cross-team activity feed. Admin-only: it spans every rep's
							    work, which no manager-level role may see wholesale. */}
							<Route
								path='/sales/activity'
								element={
									<ProtectedRoute allowedRoles={['master_admin']}>
										<SalesActivityTrackerPage />
									</ProtectedRoute>
								}
							/>
							{/* Recycle bin for soft-deleted leads. Admin-only, matching the
							    delete/restore endpoints behind it. */}
							<Route
								path='/sales/deleted'
								element={
									<ProtectedRoute allowedRoles={['master_admin']}>
										<DeletedLeadsPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/sales/dashboard'
								element={
									<ProtectedRoute
										allowedRoles={[
											'salesperson',
											'sales_manager',
											'master_admin',
										]}>
										<SalesDashboardPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/sales/appointments'
								element={
									<ProtectedRoute
										allowedRoles={[
											'salesperson',
											'sales_manager',
											'appointment_coordinator',
											'master_admin',
										]}>
										<SalesAppointmentsPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/sales/assistant'
								element={
									// Admin-only for now: the entry points for the other
									// sales roles were removed, so the guard matches.
									<ProtectedRoute
										allowedRoles={[
											'master_admin',
										]}>
										<SalesAssistantPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/sales/contact-list'
								element={
									<ProtectedRoute
										allowedRoles={[
											'appointment_coordinator',
											'sales_manager',
											'master_admin',
										]}>
										<SalesContactListPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/sales/coordinators'
								element={
									<ProtectedRoute
										allowedRoles={['sales_manager', 'master_admin']}>
										<SalesCoordinatorsPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/sales/pipeline'
								element={
									<ProtectedRoute
										allowedRoles={[
											'salesperson',
											'sales_manager',
											'master_admin',
										]}>
										<SalesPipelinePage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/sales/leads/:id'
								element={
									<ProtectedRoute
										allowedRoles={[
											'salesperson',
											'sales_manager',
											'appointment_coordinator',
											'master_admin',
										]}>
										<SalesLeadDetailPage />
									</ProtectedRoute>
								}
							/>

							<Route
								path='/sales/proposals'
								element={
									<ProtectedRoute
										allowedRoles={[
											'salesperson',
											'sales_manager',
											'master_admin',
										]}>
										<SalesProposalsPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/sales/mou'
								element={
									<ProtectedRoute
										allowedRoles={[
											'salesperson',
											'sales_manager',
											'master_admin',
										]}>
										<SalesMousPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/sales/import'
								element={
									<ProtectedRoute
										allowedRoles={[
											'sales_manager',
											'appointment_coordinator',
											'master_admin',
										]}>
										<SalesImportPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/sales/unassigned'
								element={
									<ProtectedRoute
										allowedRoles={['sales_manager', 'master_admin']}>
										<SalesUnassignedPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/sales/zones'
								element={
									<ProtectedRoute
										allowedRoles={['sales_manager', 'master_admin']}>
										<SalesZonesPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/sales/approvals'
								element={
									<ProtectedRoute
										allowedRoles={['sales_manager', 'master_admin']}>
										<SalesApprovalsPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/sales/team'
								element={
									<ProtectedRoute
										allowedRoles={['sales_manager', 'master_admin']}>
										<SalesTeamPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/sales/lab-types'
								element={
									<ProtectedRoute
										allowedRoles={['sales_manager', 'master_admin']}>
										<SalesLabTypesPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/sales/content'
								element={
									<ProtectedRoute
										allowedRoles={[
											'salesperson',
											'sales_manager',
											'master_admin',
										]}>
										<SalesContentPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/sales/playbook'
								element={
									<ProtectedRoute
										allowedRoles={[
											'salesperson',
											'sales_manager',
											'master_admin',
										]}>
										<SalesPlaybookPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/sales/revival'
								element={
									<ProtectedRoute
										allowedRoles={[
											'salesperson',
											'sales_manager',
											'appointment_coordinator',
											'master_admin',
										]}>
										<SalesRevivalPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/sales/handoff'
								element={
									<ProtectedRoute allowedRoles={['master_admin']}>
										<SalesHandoffPage />
									</ProtectedRoute>
								}
							/>

							{/* Student */}
							<Route path='/student/login' element={<StudentLoginPage />} />
							<Route
								path='/student/dashboard'
								element={
									<ProtectedRoute allowedRoles={['student']}>
										<StudentDashboard />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/student/curricula'
								element={
									<ProtectedRoute allowedRoles={['student']}>
										<StudentCurriculaPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/student/curricula/:id'
								element={
									<ProtectedRoute allowedRoles={['student']}>
										<StudentCurriculumDetailPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/student/session/:sessionId'
								element={
									<ProtectedRoute allowedRoles={['student']}>
										<StudentSessionPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/student/profile'
								element={
									<ProtectedRoute allowedRoles={['student']}>
										<StudentProfilePage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/student/timetable'
								element={
									<ProtectedRoute allowedRoles={['student']}>
										<StudentTimetablePage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/student/lock-screen'
								element={
									<ProtectedRoute allowedRoles={['student']}>
										<StudentSessionLockScreenPage />
									</ProtectedRoute>
								}
							/>

							{/* Parent */}
							<Route path='/parent/login' element={<ParentLoginPage />} />
							<Route
								path='/parent/change-password'
								element={<ParentChangePasswordPage />}
							/>
							<Route
								path='/parent/dashboard'
								element={
									<ProtectedRoute allowedRoles={['parent']}>
										<ParentDashboard />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/parent/analytics'
								element={
									<ProtectedRoute allowedRoles={['parent']}>
										<ParentAnalyticsPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/parent/timetable'
								element={
									<ProtectedRoute allowedRoles={['parent']}>
										<ParentTimetablePage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/parent/child/:childId/session/:sessionId/review'
								element={
									<ProtectedRoute allowedRoles={['parent']}>
										<ChildSessionReviewPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/parent/child/:childId/curricula'
								element={
									<ProtectedRoute allowedRoles={['parent']}>
										<ChildCurriculumProgressPage />
									</ProtectedRoute>
								}
							/>

							{/* Trainer */}
							<Route path='/trainer/login' element={<TrainerLoginPage />} />
							<Route
								path='/trainer/dashboard'
								element={
									<ProtectedRoute allowedRoles={['trainer']}>
										<TrainerDashboard />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/trainer/classes'
								element={
									<ProtectedRoute allowedRoles={['trainer']}>
										<TrainerClassesPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/trainer/timetable'
								element={
									<ProtectedRoute allowedRoles={['trainer']}>
										<TrainerTimetableTodayPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/trainer/timetable/week'
								element={
									<ProtectedRoute allowedRoles={['trainer']}>
										<TrainerTimetableWeekPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/trainer/my-day'
								element={
									<ProtectedRoute allowedRoles={['trainer']}>
										<TrainerDayReportPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/trainer/session/:slotId'
								element={
									<ProtectedRoute allowedRoles={['trainer']}>
										<TrainerSessionFlowPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/trainer/security'
								element={
									<ProtectedRoute allowedRoles={['trainer']}>
										<TrainerSecuritySetupPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/trainer/class/:classId'
								element={
									<ProtectedRoute allowedRoles={['trainer']}>
										<TrainerClassDetail />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/trainer/student/:studentId'
								element={
									<ProtectedRoute allowedRoles={['trainer']}>
										<TrainerStudentDetail />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/trainer/curricula'
								element={
									<ProtectedRoute allowedRoles={['trainer']}>
										<TrainerCurriculaPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/trainer/curricula/:id'
								element={
									<ProtectedRoute allowedRoles={['trainer']}>
										<TrainerCurriculumDetailPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/trainer/curricula/:id/preview/:modIdx/:sesIdx'
								element={
									<ProtectedRoute allowedRoles={['trainer']}>
										<TrainerSessionPreviewPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/trainer/student/:studentId/analytics'
								element={
									<ProtectedRoute allowedRoles={['trainer']}>
										<TrainerStudentAnalyticsPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/trainer/student/:studentId/session/:sessionId/review'
								element={
									<ProtectedRoute allowedRoles={['trainer']}>
										<TrainerStudentSessionReviewPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/trainer/student/:studentId/curricula'
								element={
									<ProtectedRoute allowedRoles={['trainer']}>
										<TrainerStudentCurriculumProgressPage />
									</ProtectedRoute>
								}
							/>

							{/* School Admin */}
							<Route path='/school/login' element={<SchoolAdminLoginPage />} />
							<Route
								path='/school/setup'
								element={
									<ProtectedRoute
										allowedRoles={['school_admin']}
										requiresSetup={true}>
										<SchoolAdminSetupPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/school/dashboard'
								element={
									<ProtectedRoute allowedRoles={['school_admin']}>
										<SchoolAdminDashboard />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/school/classes'
								element={
									<ProtectedRoute allowedRoles={['school_admin']}>
										<SchoolClassesPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/school/trainers'
								element={
									<ProtectedRoute allowedRoles={['school_admin']}>
										<SchoolTrainersPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/school/students'
								element={
									<ProtectedRoute allowedRoles={['school_admin']}>
										<SchoolStudentsPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/school/analytics'
								element={
									<ProtectedRoute allowedRoles={['school_admin']}>
										<SchoolAnalyticsPage />
									</ProtectedRoute>
								}
							/>
							{/* ─── Zonal Manager (PRD §2) ─── */}
							<Route path='/zonal/login' element={<ZonalLoginPage />} />
							<Route
								path='/zonal/change-password'
								element={
									// requiresSetup marks this AS the setup screen, so the
									// mustChangePassword redirect does not fire here.
									<ProtectedRoute
										allowedRoles={['zonal_manager']}
										requiresSetup>
										<ZonalChangePasswordPage />
									</ProtectedRoute>
								}
							/>

							<Route
								path='/school/lab-approval'
								element={
									<ProtectedRoute allowedRoles={['school_admin']}>
										<SchoolLabApprovalPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/school/reports'
								element={
									<ProtectedRoute allowedRoles={['school_admin']}>
										<SchoolDailyReportsPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/school/timetable'
								element={
									<ProtectedRoute allowedRoles={['school_admin']}>
										<SchoolTimetablePage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/school/monitoring'
								element={
									<ProtectedRoute allowedRoles={['school_admin']}>
										<SchoolMonitoringPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/school/analytics/class/:classId/curriculum/:curriculumId'
								element={
									<ProtectedRoute allowedRoles={['school_admin']}>
										<SchoolClassCurriculumProgressPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/school/students/:studentId/session/:sessionId/review'
								element={
									<ProtectedRoute allowedRoles={['school_admin']}>
										<SchoolStudentSessionReviewPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/school/students/:studentId/curricula'
								element={
									<ProtectedRoute allowedRoles={['school_admin']}>
										<SchoolStudentCurriculumProgressPage />
									</ProtectedRoute>
								}
							/>

							{/* Master Admin */}
							<Route path='/admin/login' element={<MasterAdminLoginPage />} />
							<Route
								path='/admin/dashboard'
								element={
									<ProtectedRoute allowedRoles={['master_admin', 'zonal_manager']}>
										<MasterAdminDashboard />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/admin/schools'
								element={
									<ProtectedRoute allowedRoles={['master_admin', 'zonal_manager']}>
										<AdminSchoolsPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/admin/schools/:id'
								element={
									<ProtectedRoute allowedRoles={['master_admin', 'zonal_manager']}>
										<AdminSchoolDetailPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/admin/users'
								element={
									<ProtectedRoute allowedRoles={['master_admin']}>
										<AdminUsersPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/admin/curriculum'
								element={
									<ProtectedRoute allowedRoles={['master_admin', 'zonal_manager']}>
										<AdminCurriculumPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/admin/curriculum/:id'
								element={
									<ProtectedRoute allowedRoles={['master_admin', 'zonal_manager']}>
										<AdminCurriculumDetailPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/admin/curriculum/:id/preview/:modIdx/:sesIdx'
								element={
									<ProtectedRoute allowedRoles={['master_admin', 'zonal_manager']}>
										<AdminSessionPreviewPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/admin/secure-docs'
								element={
									<ProtectedRoute allowedRoles={['master_admin']}>
										<SecureDocsPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/admin/demo-requests'
								element={
									<ProtectedRoute allowedRoles={['master_admin']}>
										<DemoRequestsPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/admin/analytics'
								element={
									<ProtectedRoute allowedRoles={['master_admin', 'zonal_manager']}>
										<AdminAnalyticsPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/admin/analytics/class/:classId/curriculum/:curriculumId'
								element={
									<ProtectedRoute allowedRoles={['master_admin', 'zonal_manager']}>
										<AdminClassCurriculumProgressPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/admin/students/:studentId/session/:sessionId/review'
								element={
									<ProtectedRoute allowedRoles={['master_admin', 'zonal_manager']}>
										<AdminStudentSessionReviewPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/admin/students/:studentId/curricula'
								element={
									<ProtectedRoute allowedRoles={['master_admin', 'zonal_manager']}>
										<AdminStudentCurriculumProgressPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/admin/audit-logs'
								element={
									<ProtectedRoute allowedRoles={['master_admin']}>
										<AdminAuditLogsPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/admin/daily-reports'
								element={
									<ProtectedRoute allowedRoles={['master_admin', 'zonal_manager']}>
										<AdminDailyReportsPage />
									</ProtectedRoute>
								}
							/>
							<Route
								// Shared review screen: the API slice picks the /zonal or
								// /master-admin prefix from the role, and the server scopes
								// a manager to their own schools.
								path='/admin/daily-reports/:id'
								element={
									<ProtectedRoute
										allowedRoles={['master_admin', 'zonal_manager']}>
										<AdminDailyReportReviewPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/admin/gallery'
								element={
									<ProtectedRoute allowedRoles={['master_admin']}>
										<AdminGalleryPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/admin/timetable'
								element={
									<ProtectedRoute allowedRoles={['master_admin', 'zonal_manager']}>
										<AdminTimetablePage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/admin/monitoring'
								element={
									<ProtectedRoute allowedRoles={['master_admin', 'zonal_manager']}>
										<AdminMonitoringPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/admin/requests'
								element={
									<ProtectedRoute allowedRoles={['master_admin', 'zonal_manager']}>
										<AdminRequestsPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/admin/config'
								element={
									<ProtectedRoute allowedRoles={['master_admin']}>
										<AdminConfigPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/admin/kits'
								element={
									<ProtectedRoute allowedRoles={['master_admin']}>
										<AdminKitCatalogPage />
									</ProtectedRoute>
								}
							/>
							{/* Operations module — master admin + zonal manager */}
							<Route path='/ops' element={<Navigate to='/ops/dashboard' replace />} />
							<Route
								path='/ops/dashboard'
								element={
									<ProtectedRoute allowedRoles={['master_admin', 'zonal_manager']}>
										<OpsDashboardPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/ops/handovers'
								element={
									<ProtectedRoute allowedRoles={['master_admin', 'zonal_manager']}>
										<OpsHandoverQueuePage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/ops/handovers/:id'
								element={
									<ProtectedRoute allowedRoles={['master_admin', 'zonal_manager']}>
										<OpsHandoverDetailPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/ops/onboarding'
								element={
									<ProtectedRoute allowedRoles={['master_admin', 'zonal_manager']}>
										<OpsOnboardingPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/ops/onboarding/:schoolId'
								element={
									<ProtectedRoute allowedRoles={['master_admin', 'zonal_manager']}>
										<OpsOnboardingPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/ops/lab-setup'
								element={
									<ProtectedRoute allowedRoles={['master_admin', 'zonal_manager']}>
										<OpsLabSetupPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/ops/lab-setup/:schoolId'
								element={
									<ProtectedRoute allowedRoles={['master_admin', 'zonal_manager']}>
										<OpsLabSetupDetailPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/ops/inventory'
								element={
									<ProtectedRoute allowedRoles={['master_admin', 'zonal_manager']}>
										<OpsInventoryPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/ops/inventory/:schoolId'
								element={
									<ProtectedRoute allowedRoles={['master_admin', 'zonal_manager']}>
										<OpsInventoryPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/ops/tickets'
								element={
									<ProtectedRoute allowedRoles={['master_admin', 'zonal_manager']}>
										<OpsTicketsPage />
									</ProtectedRoute>
								}
							/>
							<Route
								path='/ops/tickets/:id'
								element={
									<ProtectedRoute allowedRoles={['master_admin', 'zonal_manager']}>
										<OpsTicketDetailPage />
									</ProtectedRoute>
								}
							/>
						</Routes>
					</Suspense>
				</BrowserRouter>
			</AuthProvider>
		</ModalProvider>
	);
}

export default App;
